import React from 'react';
import { ButtonGroup, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { FieldArray, Formik } from 'formik';
import * as yup from 'yup';
import { Button } from '@components';
import { messageError } from '../../../../utils/define';

export const emptyProduct = {
    code: '', name: '', unit: '', quantity: 0, price: 0,
    vatRate: 0, vatAmount: 0, total: 0, amount: 0
};
export const emptyInvoice = {
    lichSuThanhToanId: '', erpid: '', newERPID: '', serial: '', pattern: '',
    arisingDate: '', cusCode: '', cusName: '', cusCompanyName: '', cusEmail: '',
    cusAddress: '', cusBankNo: '', cusBankName: '', cusFax: '', cusPhone: '',
    cusTaxCode: '', paymentMethod: '', createBy: '', soPT: '', soCCCDan: '',
    mdvqhnSach: '', adjType: '', products: [], vatRate: 0, total: 0,
    vatAmount: 0, discountPercent: 0, discountAmount: 0, amount: 0,
    amountInWords: '', note: ''
};

const schema = yup.object({
    erpid: yup.string().required(messageError.emptyMessage),
    serial: yup.string().required(messageError.emptyMessage),
    pattern: yup.string().required(messageError.emptyMessage),
    products: yup.array().of(yup.object({ name: yup.string().required(messageError.emptyMessage) }))
});
const fields = [
    ['ERP ID', 'erpid'], ['ERP ID mới', 'newERPID'], ['Ký hiệu', 'serial'],
    ['Mẫu số', 'pattern'], ['Ngày hóa đơn', 'arisingDate', 'date'],
    ['Mã khách hàng', 'cusCode'], ['Tên khách hàng', 'cusName'],
    ['Tên công ty', 'cusCompanyName'], ['Email', 'cusEmail', 'email'],
    ['Địa chỉ', 'cusAddress'], ['Số tài khoản', 'cusBankNo'],
    ['Ngân hàng', 'cusBankName'], ['Fax', 'cusFax'], ['Điện thoại', 'cusPhone'],
    ['Mã số thuế', 'cusTaxCode'], ['Phương thức thanh toán', 'paymentMethod'],
    ['Người tạo', 'createBy'], ['Số PT', 'soPT'], ['Số CCCD', 'soCCCDan'],
    ['Mã ĐVQHNS', 'mdvqhnSach'], ['Loại điều chỉnh', 'adjType', 'number']
];
const totals = [
    ['Thuế suất VAT', 'vatRate'], ['Tổng tiền', 'total'], ['Tiền VAT', 'vatAmount'],
    ['Phần trăm giảm giá', 'discountPercent'], ['Tiền giảm giá', 'discountAmount'],
    ['Thành tiền', 'amount']
];
const get = (source, camel, pascal) => source?.[camel] ?? source?.[pascal];
const pascalName = (key) => key === 'erpid' ? 'ERPID'
    : key === 'newERPID' ? 'NewERPID'
        : key === 'mdvqhnSach' ? 'MDVQHNSach'
            : key.charAt(0).toUpperCase() + key.slice(1);

export const normalizeInvoice = (source = {}) => {
    const invoice = { ...emptyInvoice };
    Object.keys(invoice).forEach((key) => {
        const value = get(source, key, pascalName(key));
        if (value !== undefined && key !== 'products') invoice[key] = value;
    });
    const products = get(source, 'products', 'Products');
    invoice.products = (products?.$values || products || []).map((sourceProduct) => {
        const product = { ...emptyProduct };
        Object.keys(product).forEach((key) => {
            const value = get(sourceProduct, key, pascalName(key));
            if (value !== undefined) product[key] = value;
        });
        return product;
    });
    return invoice;
};

const HoaDonThanhToanForm = ({ title, initialValues, submitText, isSubmitting, onSubmit, onCancel }) => (
    <Card>
        <Card.Header as="h5">{title}</Card.Header>
        <Card.Body>
            <Formik enableReinitialize validationSchema={schema} initialValues={initialValues} onSubmit={onSubmit}>
                {({ handleSubmit, handleChange, values, errors, touched }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row>
                            {fields.map(([label, name, type = 'text']) => (
                                <Col md={6} key={name}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{label}</Form.Label>
                                        <Form.Control type={type} name={name} value={values[name] ?? ''}
                                            onChange={handleChange} isInvalid={touched[name] && !!errors[name]} />
                                        <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>
                        <FieldArray name="products">
                            {({ push, remove }) => (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6>Sản phẩm</h6>
                                        <Button type="button" theme="success" onClick={() => push({ ...emptyProduct })}>Thêm sản phẩm</Button>
                                    </div>
                                    <div className="table-responsive">
                                        <Table bordered size="sm">
                                            <thead><tr>
                                                <th>Mã</th><th>Tên</th><th>ĐVT</th><th>SL</th><th>Đơn giá</th>
                                                <th>VAT %</th><th>Tiền VAT</th><th>Tổng</th><th>Thành tiền</th><th />
                                            </tr></thead>
                                            <tbody>
                                                {values.products.length === 0
                                                    ? <tr><td colSpan={10} className="text-center">Chưa có sản phẩm</td></tr>
                                                    : values.products.map((product, index) => (
                                                        <tr key={index}>
                                                            {Object.keys(emptyProduct).map((name) => (
                                                                <td key={name}>
                                                                    <Form.Control size="sm"
                                                                        type={['quantity', 'price', 'vatRate', 'vatAmount', 'total', 'amount'].includes(name) ? 'number' : 'text'}
                                                                        name={`products.${index}.${name}`} value={product[name] ?? ''}
                                                                        onChange={handleChange} />
                                                                </td>
                                                            ))}
                                                            <td><Button type="button" theme="danger" onClick={() => remove(index)}>Xóa</Button></td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </FieldArray>
                        <Row>
                            {totals.map(([label, name]) => (
                                <Col md={4} key={name}><Form.Group className="mb-3">
                                    <Form.Label>{label}</Form.Label>
                                    <Form.Control type="number" name={name} value={values[name] ?? 0} onChange={handleChange} />
                                </Form.Group></Col>
                            ))}
                        </Row>
                        <Form.Group className="mb-3"><Form.Label>Số tiền bằng chữ</Form.Label>
                            <Form.Control name="amountInWords" value={values.amountInWords} onChange={handleChange} />
                        </Form.Group>
                        <Form.Group className="mb-3"><Form.Label>Ghi chú</Form.Label>
                            <Form.Control as="textarea" rows={3} name="note" value={values.note} onChange={handleChange} />
                        </Form.Group>
                        <ButtonGroup>
                            <Button type="submit" theme="primary" disabled={isSubmitting}>{submitText}</Button>
                            &nbsp;&nbsp;&nbsp;<Button type="button" theme="secondary" onClick={onCancel}>Hủy</Button>
                        </ButtonGroup>
                    </Form>
                )}
            </Formik>
        </Card.Body>
    </Card>
);

export default HoaDonThanhToanForm;
